import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from 'api-client/ApiClient'
import { cacheActions } from 'core/caching/cacheReducers'
import useDataLoader from 'core/hooks/useDataLoader'
import { loadServiceCatalog } from 'k8s/components/apiAccess/endpoints/actions'
import DataKeys from 'k8s/DataKeys'
import { ServiceStatus } from './constant'
import type { ServiceState } from './helper'

export const ServiceNameMap = {
  'instance-ha': 'VM Evacuation Service',
  orchestration: 'Orchestration Service',
  appcatalog: 'Application Catalog Service',
  'image (public)': 'Image Service – Public',
  'infra-optim': 'Dynamic Resource Rebalancing',
  network: 'Networking Service',
  hamgr: 'VM High Availability Service',
  volumev3: 'Persistent Storage Service',
  'image-cluster': 'Image Service – Cluster',
  metering: 'Telemetry Service',
  'load-balancer': 'Load Balancer Service',
  placement: 'Compute Placement Service',
  metric: 'Metrics Service',
  'image (admin)': 'Image Service – Admin',
  mors: 'Lease Manager Service',
  'key-manager': 'Key Management Service',
  dns: 'DNS Service',
  identity: 'Identity & Access Service',
  compute: 'Compute Service',
  resmgr: 'Resource Manager Service',
  sidekick: 'Support Bundle Service',
}

export const ServiceURLMap = {
  'instance-ha': '/',
  orchestration: '/',
  appcatalog: '/v1/repo',
  'image (public)': '/',
  'infra-optim': '/',
  network: '/',
  hamgr: '/v1/ha',
  volumev3: '/',
  'image-cluster': '/',
  metering: '/',
  'load-balancer': '/',
  placement: '/',
  metric: '/',
  'image (admin)': '/',
  mors: '/v1/tenant',
  'key-manager': '/',
  dns: '/',
  identity: '/',
  compute: '/',
  resmgr: '/healthz/liveness',
  sidekick: '/healthz/liveness',
}

const { keystone } = ApiClient.getInstance()

const defaultServiceHealthStats: ServiceState = {
  total: 0,
  healthy: 0,
  unhealthy: 0,
  warning: 0,
}

// 5 minutes Minimum interval between reloads in milliseconds
const MIN_RELOAD_INTERVAL = 5 * 60 * 1000

// Helper: Determine if a status is a warning status
export const isWarningStatus = (status: ServiceStatus) =>
  status === ServiceStatus.NetworkError || status === ServiceStatus.CertificateError

// Helper: Determine the status category for stats tracking
export const getStatusCategory = (status: string): 'healthy' | 'warning' | 'unhealthy' => {
  if (status === ServiceStatus.Healthy) return 'healthy'
  if (status === ServiceStatus.NetworkError || status === ServiceStatus.CertificateError)
    return 'warning'
  return 'unhealthy'
}

// Helper: Parse error response to determine status and message
export const parseErrorResponse = (error: any): { status: ServiceStatus; errorMsg: string } => {
  // 300 status is treated as healthy
  if (error?.err?.status === 300) {
    return { status: ServiceStatus.Healthy, errorMsg: '-' }
  }

  const isCertificateError = error?.err?.message?.includes('Network Error')
  const isNetworkError =
    error?.err?.message?.includes('ECONNABORTED') || error?.err?.code?.includes('ERR_NETWORK')

  const status = isCertificateError
    ? ServiceStatus.CertificateError
    : isNetworkError
      ? ServiceStatus.NetworkError
      : ServiceStatus.Unhealthy

  const errorMsg = error?.err?.message || error?.message || '-'

  return { status, errorMsg }
}

/**
 * @summary useControlPlaneServicesHealth is a custom hook that provides the service health data.
 * This hook is used to get the service endpoints from service catalog.
 * Then it checks the health of each service.
 * The hidden services are not checked for health.
 * There are 4 categories of services status:
 * 1. Healthy: The service is healthy.
 * 2. Unhealthy: The service is unhealthy.
 * 3. Error: The service is in error state.
 * 4. CertificateError: The service is in certificate error state.
 * @returns The service health data.
 */
const useControlPlaneServicesHealth = () => {
  const dispatch = useDispatch()
  const hiddenServices = [
    'cloudformation',
    'regioninfo',
    'gnocchi',
    'ceilometer',
    'image-cluster',
    'orchestration',
    'appcatalog',
    'instance-ha',
  ]
  const handleServiceDifferently = ['instance-ha', 'volumev3', 'compute']
  const [serviceCatalog, loading, loadData] = useDataLoader(loadServiceCatalog)
  const [servicesList, setServicesList] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [serviceHealthStates, setServiceHealthStates] =
    useState<ServiceState>(defaultServiceHealthStats)
  const [checkingServices, setCheckingServices] = useState<Set<string>>(new Set())
  // Guards against a stale in-flight health-check batch (e.g. mount + parent-triggered
  // reload both firing) still incrementing serviceHealthStates after a newer batch reset it.
  const activeRunIdRef = useRef(0)

  // Load the service catalog on component mount to get fresh data
  useEffect(() => {
    loadData(true)
  }, [])

  // Update servicesList when serviceCatalog changes
  useEffect(() => {
    if (serviceCatalog.length > 0) {
      const filteredServices = serviceCatalog
        .filter(
          (service) =>
            !hiddenServices.includes(service.type) && !hiddenServices.includes(service.name),
        )
        .map((service) => {
          if (handleServiceDifferently.includes(service.type)) {
            return {
              ...service,
              url: `${service.url.split('/v').slice(0, -1).join('/v')}/`,
              status: 'Loading',
              error: '-',
              displayName: ServiceNameMap[service.type],
            }
          }
          return {
            ...service,
            status: 'Unknown',
            error: '-',
            displayName: ServiceNameMap[service.type],
          }
        })
      checkServiceHealth(filteredServices)
    }
  }, [serviceCatalog])

  // Helper: Update service in list at given index
  const updateServiceAtIndex = useCallback(
    (index: number, status: ServiceStatus, error: string) => {
      setServicesList((prev) => prev.map((s, i) => (i === index ? { ...s, status, error } : s)))
    },
    [],
  )

  // Helper: Remove service from checking set
  const removeFromChecking = useCallback((serviceType: string) => {
    setCheckingServices((prev) => {
      const next = new Set(prev)
      next.delete(serviceType)
      return next
    })
  }, [])

  // Helper: Increment stats for a given category
  const incrementStat = useCallback((category: 'healthy' | 'warning' | 'unhealthy') => {
    setServiceHealthStates((prev) => ({
      ...prev,
      [category]: prev[category] + 1,
    }))
  }, [])

  // Check service health for each service and update the status
  const checkServiceHealth = useCallback(
    async (servicesList: any) => {
      if (servicesList.length === 0) {
        setIsLoading(false)
        return
      }

      // Set all services to 'Loading' status initially
      const servicesWithLoadingStatus = servicesList.map((service) => ({
        ...service,
        status: ServiceStatus.Loading,
        error: '-',
      }))

      setServicesList(servicesWithLoadingStatus)
      setCheckingServices(new Set(servicesList.map((s) => s.type)))
      setServiceHealthStates({ healthy: 0, unhealthy: 0, warning: 0, total: servicesList.length })
      setIsLoading(false)

      const runId = ++activeRunIdRef.current

      // Process each service health check
      const promises = servicesList.map((service, index) => {
        return keystone
          .checkServiceHealth(ServiceURLMap[service?.type], service?.url.split(',')[0])
          .then(() => {
            if (activeRunIdRef.current !== runId) return
            updateServiceAtIndex(index, ServiceStatus.Healthy, '-')
            removeFromChecking(service.type)
            incrementStat('healthy')
          })
          .catch((error) => {
            if (activeRunIdRef.current !== runId) return
            const { status, errorMsg } = parseErrorResponse(error)
            updateServiceAtIndex(index, status, errorMsg)
            removeFromChecking(service.type)
            incrementStat(
              isWarningStatus(status)
                ? 'warning'
                : status === ServiceStatus.Healthy
                  ? 'healthy'
                  : 'unhealthy',
            )
          })
      })

      await Promise.allSettled(promises)
      if (activeRunIdRef.current === runId) setLastUpdated(Date.now())
    },
    [updateServiceAtIndex, removeFromChecking, incrementStat],
  )

  // Helper: Update stats when transitioning from one status to another
  const updateStatsForTransition = useCallback(
    (previousStatus: string, newStatus: ServiceStatus) => {
      const prevCategory = getStatusCategory(previousStatus)
      const newCategory = getStatusCategory(newStatus)

      // No change if same category
      if (prevCategory === newCategory) return

      setServiceHealthStates((prev) => ({
        ...prev,
        [prevCategory]: Math.max(0, prev[prevCategory] - 1),
        [newCategory]: prev[newCategory] + 1,
      }))
    },
    [],
  )

  // Reload a single service's health status
  const reloadService = useCallback(
    async (serviceType: string) => {
      const serviceIndex = servicesList.findIndex((s) => s.type === serviceType)
      if (serviceIndex === -1) return

      const service = servicesList[serviceIndex]
      const previousStatus = service.status

      // Mark as checking
      setCheckingServices((prev) => new Set(prev).add(serviceType))

      try {
        await keystone.checkServiceHealth(ServiceURLMap[service?.type], service?.url.split(',')[0])
        updateServiceAtIndex(serviceIndex, ServiceStatus.Healthy, '-')
        updateStatsForTransition(previousStatus, ServiceStatus.Healthy)
      } catch (error) {
        const { status, errorMsg } = parseErrorResponse(error)
        updateServiceAtIndex(serviceIndex, status, errorMsg)
        updateStatsForTransition(previousStatus, status)
      } finally {
        removeFromChecking(serviceType)
      }
    },
    [servicesList, updateServiceAtIndex, updateStatsForTransition, removeFromChecking],
  )

  // Reload the service catalog and check service health
  const reload = useCallback(
    async (showLoader = false, forceReload = false) => {
      if (lastUpdated && Date.now() - lastUpdated < MIN_RELOAD_INTERVAL && !forceReload) return

      if (showLoader) setIsLoading(true)
      await loadData(true)
      return Promise.resolve()
    },
    [loadData, lastUpdated],
  )

  useEffect(() => {
    if (lastUpdated) {
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.ControlPlaneServicesHealth,
          lastRefreshTime: lastUpdated,
        }),
      )
    }
  }, [lastUpdated])

  const imageAdminEndpoints = useMemo(
    () =>
      serviceCatalog
        .find((service) => service.type === 'image-cluster')
        ?.url?.split(',')
        ?.map((endpoint) => endpoint.trim()),
    [serviceCatalog],
  )

  return {
    serviceHealthStates,
    servicesList,
    isLoading,
    checkingServices,
    reload,
    reloadService,
    lastUpdated,
    imageAdminEndpoints,
  }
}

export default useControlPlaneServicesHealth
